import { translationEn, translationRu } from '@cc/schema';

/**
 * E36 · the portal's side of the Values tools — the field lists the data provider asks for, the shapes it gets back, and the pure
 * helpers the hub tab uses. The rules themselves (the marks 0–10, every sector rated, who may rate with the client) are the
 * decider's; the hub only shapes what it sends.
 */
export type ProfileAction = {
  actionId: string;
  action: string;
  dueAt: string;
  assignmentId: string;
  status: 'open' | 'done';
};
export type ProfileActivity = {
  activityId: string;
  activity: string;
  difficulty: number;
  status: string;
};
export type ProfileLink = { linkId: string; focusType: string; focus: string };

export type ProfileValue = {
  value: string;
  rank: number | null;
  cardText: string | null;
  wheelSector: string | null;
  wheelSectorName: string | null;
  wheelPreset: string | null;
  current: number | null;
  ideal: number | null;
  gap: number | null;
  vlqArea: string | null;
  importance: number | null;
  consistency: number | null;
  vlqGap: number | null;
  gapArea: string | null;
  vlqGapArea: string | null;
  /** E53: set only on the value that is the client's chosen way of living. */
  rose: { strategyId: string; name: string; cappedMean: number | null } | null;
  actions: ProfileAction[];
  activities: ProfileActivity[];
  links: ProfileLink[];
};

export type ProfileRow = {
  subject: string;
  lastSortAt: string | null;
  wheelRatedAt: string | null;
  vlqComposite: number | null;
  values: ProfileValue[];
};

export type WheelSector = {
  sectorId: string;
  name: string;
  valueStatement: string | null;
  current: number | null;
  ideal: number | null;
  gap: number | null;
  obstacles: Array<{ obstacleId: string; obstacle: string; severity: number }>;
  actions: ProfileAction[];
};

export type WheelRow = {
  wheelId: string;
  preset: string;
  sectors: WheelSector[];
  ratedAt: string | null;
  ratedBy: string | null;
  ratedByName: string | null;
  ratedInSession: boolean;
  previousRatings: Array<{
    ratingId: string;
    ratedAt: string;
    ratedBy: string | null;
    ratedByName: string | null;
    sectors: Array<{ sectorId: string; current: number; ideal: number }>;
  }>;
};

const ACTION_FIELDS = ['actionId', 'action', 'dueAt', 'assignmentId', 'status'];

export const PROFILE_FIELDS = [
  'subject',
  'lastSortAt',
  'wheelRatedAt',
  'vlqComposite',
  {
    values: [
      'value',
      'rank',
      'cardText',
      'wheelSector',
      'wheelSectorName',
      'wheelPreset',
      'current',
      'ideal',
      'gap',
      'vlqArea',
      'importance',
      'consistency',
      'vlqGap',
      'gapArea',
      'vlqGapArea',
      { rose: ['strategyId', 'name', 'cappedMean'] },
      { actions: ACTION_FIELDS },
      { activities: ['activityId', 'activity', 'difficulty', 'status'] },
      { links: ['linkId', 'focusType', 'focus'] },
    ],
  },
];

export const WHEEL_FIELDS = [
  'wheelId',
  'preset',
  {
    sectors: [
      'sectorId',
      'name',
      'valueStatement',
      'current',
      'ideal',
      'gap',
      { obstacles: ['obstacleId', 'obstacle', 'severity'] },
      { actions: ACTION_FIELDS },
    ],
  },
  'ratedAt',
  'ratedBy',
  'ratedByName',
  'ratedInSession',
  {
    previousRatings: [
      'ratingId',
      'ratedAt',
      'ratedBy',
      'ratedByName',
      { sectors: ['sectorId', 'current', 'ideal'] },
    ],
  },
];

export const COMMAND_FIELDS = ['id', 'recorded'];

/** The exercises the Values tab assigns, as `target.type` — each completes on ITS fact (the E34 evidence matcher). */
export const VALUES_EXERCISES = [
  'valuesSort',
  'valuesWheel',
  'valuedLivingQuestionnaire',
  // E53 — the wind rose (completes on the client's own choice)
  'windRose',
] as const;
export type ValuesExercise = (typeof VALUES_EXERCISES)[number];

// ── a sector's name in the reader's language ─────────────────────────────────────────────────────

/**
 * A preset sector (the wheel's `preset` + an id the preset lists) reads in the PORTAL's language, whatever language the wheel was
 * set up in (the phone bakes its own language into the stored `name`); a custom sector is the person's own words, as stored. The
 * words are the app's `values.wheel.preset.<preset>.<sectorId>` — the preset object's keys ARE the preset's sector ids (the domain's
 * `presetSectorKey` and this agree; see model.test.ts). Display-time only.
 */
export const sectorLabel = (
  locale: 'en' | 'ru',
  preset: string | null | undefined,
  sectorId: string,
  stored: string | null | undefined,
): string => {
  if (!preset || preset === 'custom') return stored ?? '';
  const words = ((locale === 'ru' ? translationRu : translationEn) as unknown as {
    values: { wheel: { preset: Record<string, Record<string, string> | undefined> } };
  }).values.wheel.preset[preset];
  return words?.[sectorId] ?? stored ?? '';
};

// ── the in-session rating draft ────────────────────────────────────────────────────────────────

export type SectorDraft = {
  sectorId: string;
  name: string;
  valueStatement: string;
  current: number | null;
  ideal: number | null;
};

/** The rating starts from the client's last one — the therapist re-marks WITH the client, never from nothing. */
export const draftFromWheel = (wheel: WheelRow, locale: 'en' | 'ru'): SectorDraft[] =>
  wheel.sectors.map((s) => ({
    sectorId: s.sectorId,
    name: sectorLabel(locale, wheel.preset, s.sectorId, s.name),
    valueStatement: s.valueStatement ?? '',
    current: s.current,
    ideal: s.ideal,
  }));

const markOk = (n: number | null): n is number =>
  n !== null && Number.isInteger(n) && n >= 0 && n <= 10;

export const draftComplete = (draft: readonly SectorDraft[]): boolean =>
  draft.length > 0 && draft.every((s) => markOk(s.current) && markOk(s.ideal));

export const ratingInput = (draft: readonly SectorDraft[]) =>
  draft.map((s) => ({
    sectorId: s.sectorId,
    valueStatement: s.valueStatement.trim(),
    current: s.current as number,
    ideal: s.ideal as number,
  }));

/** A value's display name: the card's NAME when it is a card line, else the value as the person wrote it. */
export const cardName = (value: string, cardText: string | null): string => {
  if (!cardText) return value;
  const at = cardText.indexOf(' — ');
  return at < 0 ? cardText : cardText.slice(0, at);
};

export const cardMeaning = (cardText: string | null): string => {
  if (!cardText) return '';
  const at = cardText.indexOf(' — ');
  return at < 0 ? '' : cardText.slice(at + 3);
};

// ── the compact profile ────────────────────────────────────────────────────────────────────────

/** How many ranked values the profile shows on load (the rest behind "Show all"). */
export const PROFILE_VISIBLE = 5;

/** Ranked first (by rank), then the unranked in the order the server sent them. */
export const profileOrder = (values: readonly ProfileValue[]): ProfileValue[] => {
  const ranked = values
    .filter((v) => v.rank !== null)
    .sort((a, b) => (a.rank as number) - (b.rank as number));
  const unranked = values.filter((v) => v.rank === null);
  return [...ranked, ...unranked];
};

/** The rows visible on load: the first {@link PROFILE_VISIBLE} ranked values PLUS any row carrying the chosen way (the rose). */
export const profileVisible = (
  ordered: readonly ProfileValue[],
  showAll: boolean,
): ProfileValue[] => {
  if (showAll) return [...ordered];
  const firstRanked = ordered
    .filter((v) => v.rank !== null)
    .slice(0, PROFILE_VISIBLE);
  return ordered.filter((v) => firstRanked.includes(v) || v.rose !== null);
};

/** The Values page is empty (the one-line EmptyState) only with no values, no questionnaire score and no wheel. */
export const valuesEmpty = (
  view: { values: readonly unknown[]; vlqComposite: number | null } | undefined,
  wheel: unknown | null,
): boolean =>
  !!view &&
  view.values.length === 0 &&
  (view.vlqComposite === null || view.vlqComposite === undefined) &&
  !wheel;
